/**
 * NotificationDropdown Component
 *
 * Displays list of notifications with actions:
 * - Mark individual as read
 * - Mark all as read
 * - Delete notification
 * - Navigate to related item
 */

import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  Shield,
  Landmark,
  ClipboardList,
  FileText,
  Check,
  CheckCheck,
  Trash2,
  X,
  Clock,
  Loader2,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  warranty_expiring: {
    icon: AlertCircle,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    label: "Warranty",
  },
  insurance_expiring: {
    icon: Shield,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    label: "Insurance",
  },
  mortgage_renewal: {
    icon: Landmark,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    label: "Mortgage",
  },
  task_due: {
    icon: ClipboardList,
    color: "text-green-600",
    bgColor: "bg-green-50",
    label: "Task",
  },
  document_shared: {
    icon: FileText,
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    label: "Document",
  },
};

// Format relative time
function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationDropdown({
  userId = "owner",
  onClose,
  onNotificationRead,
  onMarkAllRead,
}) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE}/notifications/${userId}?limit=20`
        );
        if (!response.ok) throw new Error("Failed to fetch notifications");
        const data = await response.json();
        setNotifications(data.notifications);
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [userId]);

  // Mark single notification as read
  const handleMarkRead = async (notifId) => {
    try {
      const response = await fetch(
        `${API_BASE}/notifications/${notifId}/read`,
        { method: "PUT" }
      );
      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notifId ? { ...n, read: true, readAt: new Date().toISOString() } : n
          )
        );
        onNotificationRead?.();
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Mark all as read
  const handleMarkAllRead = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/notifications/${userId}/read-all`,
        { method: "PUT" }
      );
      if (response.ok) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, read: true, readAt: new Date().toISOString() }))
        );
        onMarkAllRead?.();
      }
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  // Delete notification
  const handleDelete = async (notifId) => {
    try {
      const response = await fetch(`${API_BASE}/notifications/${notifId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== notifId));
      }
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-[#262626]">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-[#CC0202] text-white rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-[#CC0202] hover:underline font-medium"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-red-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <Check className="w-8 h-8 mb-2" />
            <p>All caught up!</p>
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          notifications.map((notification) => {
            const config =
              NOTIFICATION_CONFIG[notification.type] ||
              NOTIFICATION_CONFIG.document_shared;
            const Icon = config.icon;

            return (
              <div
                key={notification.id}
                className={`relative px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  !notification.read ? "bg-blue-50/30" : ""
                }`}
              >
                {/* Unread indicator */}
                {!notification.read && (
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#CC0202] rounded-full" />
                )}

                <div className="flex gap-3">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-full ${config.bgColor} flex items-center justify-center`}
                  >
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <span
                        className={`text-xs font-medium ${config.color} uppercase tracking-wide`}
                      >
                        {config.label}
                      </span>
                      <span className="flex items-center text-xs text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatRelativeTime(notification.createdAt)}
                      </span>
                    </div>
                    <p className="font-medium text-[#262626] text-sm mt-0.5">
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                        {notification.body}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-2">
                      {!notification.read && (
                        <button
                          onClick={() => handleMarkRead(notification.id)}
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#CC0202]"
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                          Mark read
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <button className="text-sm text-[#CC0202] hover:underline font-medium w-full text-center">
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
